import {
  getAuth,
  signInAnonymously,
  signInWithPhoneNumber,
} from "firebase/auth";
import {
  UploadTask,
  deleteObject,
  getStorage,
  ref,
  uploadBytes,
  uploadBytesResumable,
  uploadString,
} from "firebase/storage";
import storageService from "./storage.service";
import utilService from "./util.service";
import { TYPE } from "vue-toastification";
import { getDownloadURL } from "firebase/storage";
import ChartHeader from "@/views/private/timeline/models/chartHeader.model";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import axios from "axios";

class FirebaseService {
  async uploadImage(file: any, fileName: string) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('filename', `images/${fileName}`);
      const response = await axios.post(
        'https://upload-image.tight-bonus-3de0.workers.dev',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        return response.data.result.variants[0]; // Return the first variant URL
      } else {
        throw new Error('Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }

  async getAuthToken() {
    const auth = getAuth();
    const data = await auth.currentUser?.getIdTokenResult();
    let user = storageService.getUser();
    if (data && user.uid) {
      storageService.setToken(data!.token);
    }
    return data?.token;
  }

  async logout() {
    const auth = getAuth();
    await auth.signOut();
  }

  async sendOTPCode(phoneNumber: string) {
    var gwin: any = window;
    const appVerifier = gwin.recaptchaVerifier;
    const auth = getAuth();
    var number = phoneNumber;
    if (!phoneNumber.startsWith("+")) {
      number = `+${phoneNumber}`;
    }
    const response = await signInWithPhoneNumber(auth, number, appVerifier)
      .then((confirmationResult) => {
        gwin.confirmationResult = confirmationResult;
        utilService.showToast(`Code is being sent to (${phoneNumber})`);
        return true
      })
      .catch((error) => {
        utilService.showToast("Error sending code please try to resend", TYPE.ERROR);
        return false
      });
      return response
  }

  async anonymousLogin() {
    try {
      const auth = getAuth();
      var loginRes = await signInAnonymously(auth);
      return null;
    } catch (e) {
      return null;
    }
  }

  async uploadBase64Image(
    base64Data: string,
    sectionName: string,
    userId: string,
    type: string
  ) {
    const storage = getStorage();
    const storageRef = ref(
      storage,
      `share_${userId}_${type}_${sectionName}.png`
    );
    try {
      const snapshot = await uploadString(storageRef, base64Data, "data_url", {
        contentType: "image/png",
      });
      const url = await getDownloadURL(snapshot.ref);
      return url;
    } catch (error) {
      throw error;
    }
  }

  async uploadBase64ImageForTimeLine(
    base64Data: string,
    feedId: string,
    userId: string,
    type: string
  ) {
    const storage = getStorage();
    const storageRef = ref(storage, `timeline_${userId}_${type}_${feedId}.png`);
    try {
      const snapshot = await uploadString(storageRef, base64Data, "data_url", {
        contentType: "image/png",
      });
      const url = await getDownloadURL(snapshot.ref);
      return url;
    } catch (error) {
      throw error;
    }
  }

  // async uploadMedia(media: any, path: string = "") {
  //   const storage = getStorage();
  //   const fileName: string = `${path}${new Date().getTime()}-${media.name}`;
  //   const storageRef = ref(storage, "images/" + fileName);
  //   const metadata = {
  //     contentType: media.type,
  //     contentDisposition: "attachment",
  //   };
  //   const uploadResponse = await uploadBytes(storageRef, media, metadata);
  //   const fileUrl = await getDownloadURL(uploadResponse.ref);
  //   return fileUrl;
  // }

  private currentUploadTask: UploadTask | null = null;

  async uploadMedia(media: File, path: string = ""): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const storage = getStorage();
      const fileName = `${path}${Date.now()}-${media.name}`;
      const storageRef = ref(storage, "images/" + fileName);
      const metadata = {
        contentType: media.type,
        contentDisposition: "attachment",
      };

      this.currentUploadTask = uploadBytesResumable(storageRef, media, metadata);

      this.currentUploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Upload is ${progress.toFixed(2)}% done`);
        },
        async (error) => {
          if (error.code === "storage/canceled") {
            try {
              await deleteObject(storageRef);
              console.log("Partially uploaded file deleted.");
            } catch (deleteErr) {
              console.warn("Failed to delete partial file:", deleteErr);
            }
          }
          reject(error);
        },
        async () => {
          const fileUrl = await getDownloadURL(this.currentUploadTask!.snapshot.ref);
          resolve(fileUrl);
        }
      );
    });
  }

  cancelUpload() {
    if (this.currentUploadTask) {
      this.currentUploadTask.cancel();
      console.log("Upload canceled.");
    } else {
      console.warn("No active upload task to cancel.");
    }
  }

  extractStoragePathFromUrl(url: string): string | null {
    const match = url.match(/\/o\/(.*?)\?/);
    if (!match || !match[1]) return null;
    return decodeURIComponent(match[1]); // converts %2F back to "/"
  }

  async deleteMediaByUrl(url: string) {
    const path = this.extractStoragePathFromUrl(url);
    if (!path) throw new Error("Invalid Firebase Storage URL.");

    const storage = getStorage();
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
    console.log("Deleted:", path);
  }

  async getChartHeader(stockId: string): Promise<ChartHeader | undefined> {
    var chartHeader: ChartHeader = new ChartHeader();
    const db = getFirestore();
    const docRef = doc(db, "stocks", stockId);
    var docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      var stock = docSnap.data();
      chartHeader = ChartHeader.copyFromStockFirebase(stock);
    }
    if (chartHeader && chartHeader.symbol) {
      return chartHeader;
    }
    return undefined;
  }

  getStockChart(
    stockId: string,
    duration: string,
    interval: string,
    chartType: string,
    dataTypes: any[] = ["Open"]
  ): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        dataTypes = dataTypes.filter((x) => x.isActive).map((y) => y.value);
        var docId = `${stockId}-${duration}-${interval}-${chartType}`;
        const db = getFirestore();
        const docRef = doc(db, "stockcharts", docId);
        var docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          var chartData = docSnap.data().chartData;
          if (chartType == "candlestick") {
            resolve(JSON.parse(chartData));
          } else {
            resolve(chartData.filter((x: any) => dataTypes.includes(x.name)));
          }
        }
      } catch (e) {}
      resolve([]);
    });
  }
  
  refreshToken() {
    const auth = getAuth();
    auth.onIdTokenChanged(async () => {});
  }

}

export default new FirebaseService();
